import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useTelegram } from '../../lib/telegram/telegram-context';
import { ONBOARDING_ROUTES, toChannelsView, useChannels, useChannelWrites } from '../onboarding';
import { readStoppedCopying } from '../settings';

import { type ChannelsNotice, ChannelsTabView } from './ChannelsTabView';
import { useCopiedSettings } from './use-copied-settings';
import { useReloadWhenVisible } from './use-reload-when-visible';

/**
 * Route element of E12 (specs/S9-channels-tab/spec.md): `GET /channels` and `GET /me` once, in
 * parallel, again when the app is visible again or on Retry (R10, no polling); "Copy" through
 * E3's writes hook (R6, R12); the removal message E7 passes in the navigation state (R16); E2 on
 * a lost session; the affiliate's channel in Telegram (S5 R7).
 */
export function ChannelsTabScreen(): ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const telegram = useTelegram();
  const channels = useChannels();
  const copied = useCopiedSettings();
  const writes = useChannelWrites({ base: channels.data, reload: channels.refetch });
  const { refetch: reloadChannels } = channels;
  const { reload: reloadSettings } = copied;
  const reload = useCallback(() => {
    reloadChannels();
    reloadSettings();
  }, [reloadChannels, reloadSettings]);
  useReloadWhenVisible(reload);
  // R16, P6: read once when E12 mounts; in memory only (navigation state, no browser storage).
  const [stopped] = useState(() => readStoppedCopying(location.state));

  const view = useMemo(() => toChannelsView(channels, writes.overlay), [channels, writes.overlay]);
  const notice: ChannelsNotice | null =
    writes.added !== null
      ? { kind: 'added', name: writes.added }
      : stopped === null
        ? null
        : { kind: 'removed', name: stopped.name };

  return (
    <ChannelsTabView
      view={view}
      settings={copied.settings}
      busy={writes.busy}
      writeFailure={writes.writeFailure}
      notice={notice}
      onCopy={writes.toggle}
      onReload={reload}
      onJoin={(url) => {
        telegram.openTelegramLink(url);
      }}
      onSignInAgain={() => {
        void navigate(ONBOARDING_ROUTES.telegram);
      }}
    />
  );
}
